import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/use-auth';
import { cn } from '../../utils';
import { VersionDisplay } from '../version-display';
import approvnLogo from '../../assets/approvn-logo.png';

export function SidebarNavigation() {
  const { isAuthenticated, isAdmin, isSupport, isWorkflowAdmin } = useAuth();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Support can see admin-level views but not configuration or simulator
  const isAdminOrSupport = isAdmin || isSupport;
  // WorkflowAdmin is narrower than Admin/Support (no Configuration/Simulator) but should see
  // everything Support sees: all orders + Workflows/Templates/Users.
  const canViewAllOrdersOrManageWorkflows = isAdminOrSupport || isWorkflowAdmin;

  const navItems = [
    { 
      name: 'Dashboard', 
      path: '/', 
      show: true, 
      icon: 'fa-solid fa-chart-line' 
    },
    { 
      name: 'All Orders', 
      path: '/all-orders', 
      show: canViewAllOrdersOrManageWorkflows, 
      icon: 'fa-solid fa-boxes-stacked' 
    },
    { 
      name: 'Action Required', 
      path: '/action-required', 
      show: isAuthenticated, 
      icon: 'fa-solid fa-clock' 
    },
    { 
      name: 'In Progress', 
      path: '/in-progress', 
      show: isAuthenticated, 
      icon: 'fa-solid fa-spinner' 
    },
    { 
      name: 'Submitted Orders', 
      path: '/submitted-orders', 
      show: canViewAllOrdersOrManageWorkflows, 
      icon: 'fa-solid fa-paper-plane' 
    },
    { 
      name: 'Paused Orders', 
      path: '/paused-orders', 
      show: isAuthenticated, 
      icon: 'fa-solid fa-pause-circle' 
    },
    { 
      name: 'Completed', 
      path: '/completed', 
      show: isAuthenticated, 
      icon: 'fa-solid fa-check-circle' 
    },
    { 
      name: 'Cancelled', 
      path: '/cancelled', 
      show: isAuthenticated, 
      icon: 'fa-solid fa-ban' 
    },
    { 
      name: 'Delegations', 
      path: '/delegations', 
      show: isAuthenticated, 
      icon: 'fa-solid fa-user-check' 
    },
    { 
      name: 'Error Queue', 
      path: '/error-queue', 
      show: canViewAllOrdersOrManageWorkflows, 
      icon: 'fa-solid fa-exclamation-triangle' 
    },
    { 
      name: 'Workflows', 
      path: '/workflows', 
      show: canViewAllOrdersOrManageWorkflows, 
      icon: 'fa-solid fa-diagram-project' 
    },
    { 
      name: 'Workflow Templates', 
      path: '/workflow-templates', 
      show: canViewAllOrdersOrManageWorkflows, 
      icon: 'fa-solid fa-layer-group' 
    },
    { 
      name: 'Users', 
      path: '/users', 
      show: canViewAllOrdersOrManageWorkflows, 
      icon: 'fa-solid fa-users' 
    },
    { 
      name: 'Configuration', 
      path: '/configuration', 
      show: isAdmin,  // Support cannot access Configuration
      icon: 'fa-solid fa-gear' 
    },
    { 
      name: 'Approver Report', 
      path: '/reports/workflow-approvers', 
      show: isAdmin, 
      icon: 'fa-solid fa-file-lines' 
    },
    { 
      name: 'Workflow Simulator', 
      path: '/simulator', 
      show: isAdmin,  // Support cannot access Simulator
      icon: 'fa-solid fa-flask' 
    },
  ];

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <aside 
      className={cn(
        'bg-white dark:bg-secondary-800 border-r border-gray-200 dark:border-secondary-700 transition-all duration-300 ease-in-out flex flex-col',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-secondary-700">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <Link to="/" className="flex items-center" aria-label="Approv.n">
              <img src={approvnLogo} alt="Approv.n" className="h-8 w-auto" />
            </Link>
          )}
          <button
            onClick={toggleSidebar}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!isCollapsed}
            className={cn(
              'p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-600 dark:hover:bg-secondary-700 transition-colors',
              isCollapsed && 'mx-auto'
            )}
          >
            <i className={cn(
              'fa-solid text-gray-700 dark:text-secondary-200 dark:text-secondary-300',
              isCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'
            )} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <div className="space-y-2">
          {navItems
            .filter(item => item.show)
            .map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'flex items-center px-3 py-2.5 rounded-lg transition-colors group relative',
                    isActive 
                      ? 'text-gray-700 dark:text-secondary-200 dark:text-secondary-100 bg-gray-100 dark:bg-secondary-700' 
                      : 'text-gray-600 dark:text-secondary-400 hover:bg-gray-50 dark:hover:bg-secondary-700 hover:text-gray-700 dark:hover:text-secondary-200 dark:hover:text-secondary-100'
                  )}
                  title={isCollapsed ? item.name : undefined}
                  aria-label={isCollapsed ? item.name : undefined}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <i className={cn(
                    item.icon,
                    'w-5 h-5 flex-shrink-0',
                    isCollapsed ? 'mx-auto' : 'mr-3'
                  )} aria-hidden="true"></i>
                  {!isCollapsed && (
                    <span className="font-medium">{item.name}</span>
                  )}
                  
                  {/* Tooltip for collapsed state */}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-secondary-950 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                      {item.name}
                    </div>
                  )}
                </Link>
              );
            })
          }
        </div>
      </nav>

      {/* Version Info Footer */}
      <VersionDisplay collapsed={isCollapsed} />

    </aside>
  );
}